import { errorMessageFrom } from '../api'
import { config } from '../config'
import { refundQuoteFailureMessage } from './refund-quote-failure-message'

const POLL_EVERY_MS = 3_000
const GIVE_UP_AFTER_MS = 3 * 60_000

export const STILL_RUNNING_MESSAGE =
  'The airline system has not answered after 3 minutes. Try again later. If it still does not answer, handle this booking manually.'

export interface Waiting {
  fetch: typeof fetch
  sleep: (ms: number) => Promise<void>
  now: () => number
  /** Aborts after the given time. */
  deadline: (ms: number) => AbortSignal
}

const realWaiting: Waiting = {
  fetch: (input, init) => fetch(input, init),
  sleep: ms => new Promise(resolve => setTimeout(resolve, ms)),
  now: () => Date.now(),
  deadline: ms => AbortSignal.timeout(ms),
}

/**
 * The airline check behind a refund quote can take longer than the gateway's 30 seconds, so it runs
 * in the background: start it, then poll until it finishes. Resolves to the quote, or throws with
 * the message to show.
 */
export async function retrieveRefundQuote(
  bookingId: string,
  { waiverCode, record = true }: { waiverCode?: string; record?: boolean } = {},
  waiting: Waiting = realWaiting,
) {
  const jobs = `${config.apiBaseUrl}/bookings/${bookingId}/refund-quote/jobs`
  // The deadline also cuts off a request still in flight, so a hung connection cannot keep the screen
  // waiting past it.
  const deadline = waiting.deadline(GIVE_UP_AFTER_MS)

  try {
    const started = await waiting.fetch(jobs, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ waiverCode }),
      signal: deadline,
    })
    if (!started.ok) throw new Error(refundQuoteFailureMessage(started.status, await errorMessageFrom(started)))

    const { jobId } = (await started.json()) as { jobId: string }
    const poll = `${jobs}/${jobId}${record ? '' : '?record=false'}`
    const giveUpAt = waiting.now() + GIVE_UP_AFTER_MS

    while (waiting.now() < giveUpAt) {
      await waiting.sleep(POLL_EVERY_MS)

      const polled = await waiting.fetch(poll, { credentials: 'include', cache: 'no-store', signal: deadline })
      if (polled.status === 202) continue

      if (!polled.ok) throw new Error(refundQuoteFailureMessage(polled.status, await errorMessageFrom(polled)))

      return await polled.json()
    }
  } catch (error) {
    if (!deadline.aborted) throw error
  }

  throw new Error(STILL_RUNNING_MESSAGE)
}
